import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import ReviewCard from '@/components/base/ReviewCard';
import { reviews, reviewTagOptions } from '@/mocks/reviews';

const PAGE_SIZE = 4;

export default function Reviews() {
  const [tag, setTag] = useState('全部');
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () => (tag === '全部' ? reviews : reviews.filter((r) => r.tags.includes(tag))),
    [tag],
  );

  const featured = filtered[0];
  const list = filtered.slice(1);
  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeTag = (value: string) => {
    setTag(value);
    setPage(1);
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-300">影评专题</span>
        </nav>

        <div className="mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading">影评专题</h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-400 leading-relaxed">
            从叙事节奏到视觉美学，从人物群像到题材边界，为你提供成人短剧的深度观看视角与专业解读。
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          {['全部', ...reviewTagOptions].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => changeTag(t)}
              className={`rounded-full px-4 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                tag === t ? 'bg-primary-600 text-background-50' : 'bg-background-900 text-foreground-300 hover:bg-background-800'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-700 bg-background-900/40 py-20">
            <i className="ri-article-line text-5xl text-foreground-600" />
            <p className="mt-4 text-foreground-300">暂无影评内容</p>
            <button
              type="button"
              onClick={() => changeTag('全部')}
              className="mt-5 rounded-md bg-primary-600 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部影评
            </button>
          </div>
        ) : (
          <>
            {featured && (
              <div className="mb-8">
                <ReviewCard review={featured} featured />
              </div>
            )}

            {paged.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {paged.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-lg" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                      p === currentPage
                        ? 'bg-primary-600 text-background-50 font-medium'
                        : 'border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-primary-300'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-lg" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}